import validate from 'validate.js';
import uuid from 'uuid';
import request from 'superagent';
import find from 'lodash-es/find';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import cloneDeepWith from 'lodash-es/cloneDeepWith';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isPlainObject from 'lodash-es/isPlainObject';
import map from 'lodash-es/map';
import pickBy from 'lodash-es/pickBy';
import set from 'lodash-es/set';
import unset from 'lodash-es/unset';
import { parse } from 'node-html-parser';

import variables from './variables';
import smalltalk from '../vendor/smalltalk';
import Color from 'color';

const GOOGLE_API_KEY = process.env.GOOGLE_API_KEY;

const ACCEPTED_DOMAINS = {
  airtable: [/https:\/\/airtable.com/],
  cloudapp: [/https:\/\/cl.ly/, /https:\/\/share.getcloudapp.com/],
  codepen: [/http:\/\/codepen.io/, /https:\/\/codepen.io/],
  dropbox: [/https:\/\/www.dropbox.com/],
  figma: [
    /https:\/\/([w.-]+.)?figma.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/,
  ], // See https://www.figma.com/platform
  flickr: [/https:\/\/www.flickr.com/],
  framer: [/https:\/\/framer.cloud/],
  giphy: [/https:\/\/giphy.com/],
  instagram: [/https:\/\/www.instagram.com/],
  invision: [
    /https:\/\/invis.io/,
    /https:\/\/projects.invisionapp.com/,
    /https:\/\/[A-Za-z0-9]+.invisionapp.com/,
  ],
  maps: [/https:\/\/goo.gl\/maps/],
  marvel: [/https:\/\/marvelapp.com/],
  protoio: [/https:\/\/share.proto.io/, /https:\/\/pr.to/],
  sketchfab: [/https:\/\/sketchfab.com/, /https:\/\/skfb.ly/],
  soundcloud: [/https:\/\/soundcloud.com/],
  streamable: [/https:\/\/streamable.com/],
  streetview: [/https:\/\/goo.gl\/maps/],
  typeform: [/https:\/\/[A-Za-z0-9]+.typeform.com\/to/],
  unsplash: [/http:\/\/unsplash.com\/photos/, /https:\/\/unsplash.com\/photos/],
  vimeo: [/https:\/\/vimeo.com/],
  wistia: [/https:\/\/[A-Za-z0-9]+.wistia.com/],
  xd: [/https:\/\/xd.adobe.com\/view/],
  youtube: [/https:\/\/www.youtube.com/, /https:\/\/youtu.be/],
};

const LABELS = {
  airtable: 'Airtable',
  cloudapp: 'CloudApp',
  codepen: 'CodePen',
  dropbox: 'Dropbox',
  figma: 'Figma',
  flickr: 'Flickr',
  framer: 'Framer',
  giphy: 'GIPHY',
  instagram: 'Instagram',
  invision: 'InVision',
  maps: 'Google Maps',
  marvel: 'Marvel',
  protoio: 'Proto.io',
  sketchfab: 'Sketchfab',
  soundcloud: 'SoundCloud',
  streamable: 'Streamable',
  streetview: 'Google Street View',
  typeform: 'Typeform',
  unsplash: 'Unsplash',
  vimeo: 'Vimeo',
  wistia: 'Wistia',
  xd: 'Adobe XD',
  youtube: 'YouTube',
  brandfetch: 'Brandfetch',
};

const DOES_NOT_GO_THROUGH_IFRAMELY = [
  'cloudapp',
  'unsplash',
  'dropbox',
  'giphy',
];

const COMPETITOR_DOMAINS = [
  'beautiful.ai',
  'canva.com',
  'pitch.io',
  'pitch.com',
  'prezi.com',
  'projector.com',
  'slidebean.com',
  'slides.com',
  'visme.co',
  'wetransfer.com',
  'zoho.com',
];

const LIST_LABELS = {
  none: 'None',
  bullet: 'Bulleted',
  ordered: 'Numbered',
  checked: 'Checked',
  unchecked: 'Unchecked',
};

const isEdge = /Edge/.test(window.navigator.userAgent);
const isChrome =
  /Chrome/.test(window.navigator.userAgent) &&
  /Google/.test(window.navigator.vendor);
const isSafari =
  /Safari/.test(window.navigator.userAgent) &&
  /Apple/.test(window.navigator.vendor);
const isFirefox = /Firefox/.test(window.navigator.userAgent);
const isIE = /MSIE|Trident/.test(window.navigator.userAgent);

const isMac = window.navigator.platform.indexOf('Mac') !== -1;
const isMobile = /Mobi/.test(window.navigator.userAgent);

const getWindowName = (app, id) => {
  const base = variables.windows[app];
  if (id) {
    return `${base}_${id}`;
  }
  return base;
};

const getEditorWindowNameForId = id => getWindowName('editor', id);

const getViewerWindowNameForId = id => getWindowName('viewer', id);

const getEmbedWindowNameForId = id => getWindowName('embed', id);

const getDashboardWindowName = () => getWindowName('dashboard');

const getSpeakerWindowName = () => getWindowName('speaker');

/**
 * In some cases (symbol creation for example), we assign unique identifier to ludus objects.
 *
 * @param obj
 */
const addLudusIds = obj => {
  if (!has(obj, 'ludus.id')) {
    set(obj, 'ludus.id', uuid.v4());
  }

  if (get(obj, 'ludus.type') === 'group' || obj.type === 'activeSelection') {
    obj.forEachObject(addLudusIds);
  }
};

const insertInTextArea = (textArea, value) => {
  // TODO: doesn't work with Quill
  const start = textArea.selectionStart;
  const end = textArea.selectionEnd;
  const text = textArea.value;
  const before = text.substring(0, start);
  const after = text.substring(end, text.length);
  set(textArea, 'value', before + value + after);
  set(textArea, 'selectionEnd', start + value.length);
  set(textArea, 'selectionStart', textArea.selectionEnd);
};

const parseJSONForTextValue = json => {
  try {
    const dataAsJson = JSON.parse(json);
    // only use the value of firt text block
    const textObject = find(dataAsJson, 'text');
    const textValue = textObject ? textObject.text : null;
    if (textValue) {
      return textValue;
    }
    if (dataAsJson.every(o => o.ludus)) {
      return dataAsJson;
    }
    // nothing related to ludus in json
    return json;
  } catch (e) {
    return json;
  }
};

const getSizeFromViewBox = viewBox => {
  if (viewBox) {
    const vbArray = viewBox.split(' ');
    return {
      width: vbArray[2],
      height: vbArray[3],
    };
  }
  return null;
};

const checkSVGString = string => {
  const dom = parse(string);
  const s = dom.querySelector('svg');
  if (!s.attributes.width) {
    let width = '300px';
    let height = '300px';
    const vb = s.attributes.viewBox;
    const size = getSizeFromViewBox(vb);
    if (size) {
      width = size.width;
      height = size.height;
    }
    s.setAttribute('width', `${width}px`);
    s.setAttribute('height', `${height}px`);
  }
  return s.toString();
};

/**
 * We might need to remove unique identifier when copy-pasting or alt-dragging,
 * or when flattening symbols.
 *
 * @param obj
 */
const removeLudusIds = obj => {
  if (get(obj, 'ludus.type') === 'group' || obj.type === 'activeSelection') {
    obj.forEachObject(removeLudusIds);
  }

  unset(obj, 'ludus.id');
};

const cleanValues = o =>
  pickBy(o, v =>
    isPlainObject(v) ? !isEmpty(v) : v !== undefined && v !== null,
  );

const cleanValuesDeep = o => {
  const cloned = cloneDeepWith(o, v =>
    v !== o && isPlainObject(v) ? cleanValuesDeep(v) : undefined,
  );
  return cleanValues(cloned);
};

const safeGet = (object, path) => {
  try {
    return get(object, path);
  } catch (error) {
    console.error(error);
  }
  return undefined;
};

const schedule = func => Promise.resolve().then(func);

// Round floats that are very close to an integer.
// 19.99 becomes 20
// 19.5 remains 19.5
const smartRound = number =>
  Number.parseFloat(Number.parseFloat(number).toFixed(1));

const advancedQuerySelector = selector => {
  let el;
  // Support parent selectors to climb up a DOM tree
  const tree = selector.split('<').map(leaf => leaf.trim());
  el = document.querySelector(tree[0]);
  tree.slice(1).forEach(leaf => {
    if (el) {
      el = el.closest(leaf);
    }
  });
  return el;
};

// String utils

const toSentence = arr => {
  const separator = ', ';
  const lastSeparator = ' and ';

  if (arr.length === 0) {
    return '';
  }

  if (arr.length === 1) {
    return arr[0];
  }

  return arr.slice(0, -1).join(separator) + lastSeparator + arr[arr.length - 1];
};

// Color utils
const parseRGBA = str => {
  if (str && typeof str === 'string') {
    if (str === 'transparent') {
      return {
        r: 0,
        g: 0,
        b: 0,
        a: 0,
      };
    }
    const rgba = Color(str)
      .rgb()
      .array();
    if (rgba) {
      return {
        r: rgba[0],
        g: rgba[1],
        b: rgba[2],
        a: rgba[3],
      };
    }
  }
  return null;
};

const getTransformCSSFromObject = object => {
  const transform = [];

  if (object.flipX) {
    transform.push('scaleX(-1)');
  }
  if (object.flipY) {
    transform.push('scaleY(-1)');
  }
  return transform.join(' ') || 'none';
};

const getContrastColor = str => {
  return Color(str).isLight() ? 'rgb(0,0,0)' : 'rgb(255,255,255)';
};

const getRGBStringFromRGBObject = rgb =>
  rgb ? `rgb(${rgb.r},${rgb.g},${rgb.b})` : 'rgb(0,0,0)';

const getRGBAStringFromRGBAObject = rgba =>
  rgba ? `rgba(${rgba.r},${rgba.g},${rgba.b},${rgba.a})` : 'rgba(0,0,0,1)';

const getInterpolatedRGBOjectFromRGBColors = (
  rgbStr1,
  rgbStr2,
  offset = 0.5,
) => {
  const rgb1 = Color(rgbStr1).object();
  const rgb2 = Color(rgbStr2).object();
  return {
    r: Math.abs(Math.floor(rgb2.r * offset + rgb1.r * (1 - offset))),
    g: Math.abs(Math.floor(rgb2.g * offset + rgb1.g * (1 - offset))),
    b: Math.abs(Math.floor(rgb2.b * offset + rgb1.b * (1 - offset))),
  };
};

const getRGBAObjectFromColorPickerObject = colorPickerObject => {
  const rgb = Color(colorPickerObject.color).object();
  const alpha = colorPickerObject.alpha / 100;
  return { ...rgb, a: alpha };
};

const getRGBAStringFromColorPickerObject = colorPickerObject =>
  getRGBAStringFromRGBAObject(
    getRGBAObjectFromColorPickerObject(colorPickerObject),
  );

const getNormalizedRGBAString = str => {
  try {
    const color =
      typeof str === 'string'
        ? str.split(',rgba')[0]
        : Array.isArray(str)
        ? str[0]
        : str;
    const parsedColor = Color(color || 'rgb(0,0,0)')
      .rgb()
      .string();
    return parsedColor;
  } catch {
    return 'rgb(0,0,0)';
  }
};

// Canvas related

const getCanvasPixelRatio = context => {
  const backingStore =
    context.backingStorePixelRatio ||
    context.webkitBackingStorePixelRatio ||
    context.mozBackingStorePixelRatio ||
    context.msBackingStorePixelRatio ||
    context.oBackingStorePixelRatio ||
    context.backingStorePixelRatio ||
    1;
  return (window.devicePixelRatio || 1) / backingStore;
};

const isValidURL = (str, allowOtherProtocols = false) => {
  if (!str) {
    return false;
  }

  const allowedProtocols = [
    { prefix: 'http', default: true, validator: str => isValidHttpURL(str) },
    { prefix: 'https', default: true, validator: str => isValidHttpURL(str) },
    {
      prefix: 'mailto',
      default: false,
      validator: str => isValidEmail(str.substr(7)),
    },
    { prefix: 'tel', default: false },
    { prefix: 'callto', default: false },
    { prefix: 'sms', default: false },
    { prefix: 'fax', default: false },
  ];
  return allowedProtocols.some(allowedProtocol => {
    return (
      str.startsWith(`${allowedProtocol.prefix}:`) &&
      (allowOtherProtocols || allowedProtocol.default) &&
      (allowedProtocol.validator ? allowedProtocol.validator(str) : true)
    );
  });
};

const isValidHttpURL = str =>
  typeof validate.single(str, { url: true }) === 'undefined';

const isValidEmail = str =>
  typeof validate.single(str, { email: true }) === 'undefined';

const getEmailsFromString = str =>
  str
    .split(/[ ,\n\r]+/)
    .filter(item => isValidEmail(item))
    .filter((v, i, a) => a.indexOf(v) === i);

const getDefaultTheme = () => {
  try {
    const ls = localStorage.getItem('theme');
    if (ls) {
      const theme = JSON.parse(ls);
      if (theme) {
        return theme;
      }
    }
  } catch {}
  try {
    const darkMatch =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    return {
      mode: 'auto',
      theme: darkMatch ? 'dark' : 'light',
    };
  } catch {}
  return {
    mode: 'auto',
    theme: 'light',
  };
};

const getUrlParameter = param => {
  const pageURL = decodeURIComponent(window.location.search.substring(1));
  const vars = pageURL.split('&');
  let parameterName;
  let i;

  for (i = 0; i < vars.length; i += 1) {
    parameterName = vars[i].split('=');
    if (parameterName[0] === param) {
      return parameterName[1] === undefined ? true : parameterName[1];
    }
  }

  return null;
};

const initHeadway = () => {
  window.HW_config = {
    selector: '#headway',
    account: 'Jn5AO7',
    callbacks: {
      onWidgetReady: function(widget) {
        window.headway = widget;
      },
    },
  };
  const script = document.createElement('script');
  script.src = '//cdn.headwayapp.co/widget.js';
  script.async = true;
  document.body.appendChild(script);
};

const nl2br = (str, isXhtml) => {
  const breakTag = isXhtml ? '<br />' : '<br>';
  return String(str).replace(
    /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
    `$1${breakTag}$2`,
  );
};

const rickRollCompetitor = email => {
  const domain = email.toLowerCase().split('@')[1];
  if (COMPETITOR_DOMAINS.includes(domain)) {
    window.location.href = 'https://www.youtube.com/watch?v=oHg5SJYRHA0';
  }
};

const utils = {
  insertInTextArea,
  parseJSONForTextValue,

  isEdge,
  isChrome,
  isSafari,
  isFirefox,
  isIE,
  isMobile,
  isMac,

  initHeadway,

  nl2br,

  rickRollCompetitor,

  getCanvasPixelRatio,

  getBrowserName: () => {
    if (isEdge) {
      return 'edge';
    }
    if (isChrome) {
      return 'chrome';
    }
    if (isSafari) {
      return 'safari';
    }
    if (isFirefox) {
      return 'firefox';
    }
    if (isIE) {
      return 'msie';
    }
    return 'unknown';
  },

  getSharedDomain: context => {
    if (
      context.type === 'team' &&
      context.current.domains.length > 0 &&
      context.current.domains[0].txtVerified &&
      context.current.domains[0].cnameVerified
    ) {
      return 'https://' + context.current.domains[0].customDomain;
    }

    return window.location.origin;
  },

  isAcceptedDomain: url =>
    Object.keys(ACCEPTED_DOMAINS)
      .map(key => ACCEPTED_DOMAINS[key])
      .reduce((acc, val) => acc.concat(val), [])
      .find(item => url.search(item) === 0),

  isAcceptedDomainForSource: (url, source) =>
    ACCEPTED_DOMAINS[source].find(item => url.search(item) === 0),

  getAcceptedDomainsForSource: source =>
    ACCEPTED_DOMAINS[source].map(item =>
      item
        .toString()
        .slice(1, -1)
        .replace(/\\\//g, '/')
        .replace('[A-Za-z0-9]+', '*'),
    ),

  getSourceFromURL: url => {
    const domains = Object.entries(ACCEPTED_DOMAINS);
    let source = '';
    for (let i = 0; i < domains.length; i += 1) {
      const domain = domains[i];
      for (let j = 0; j < domain[1].length; j += 1) {
        if (url.search(domain[1][j]) === 0) {
          source = domain[0];
          break;
        }
      }
      if (source !== '') {
        break;
      }
    }
    return source;
  },

  intersection: arrays => arrays.reduce((a, b) => a.filter(c => b.includes(c))),

  doesNotGoThroughIframely: source =>
    DOES_NOT_GO_THROUGH_IFRAMELY.indexOf(source) !== -1,

  getLabel: source => LABELS[source],

  deepCopy: object => JSON.parse(JSON.stringify(object)),

  getLocalApiUrl: url => {
    const parser = document.createElement('a');
    parser.href = url;
    return parser.pathname;
  },

  isSubDomain(url) {
    // IF THERE, REMOVE WHITE SPACE FROM BOTH ENDS
    url = url.replace(new RegExp(/^\s+/), ''); // START
    url = url.replace(new RegExp(/\s+$/), ''); // END

    // IF FOUND, CONVERT BACK SLASHES TO FORWARD SLASHES
    url = url.replace(new RegExp(/\\/g), '/');

    // IF THERE, REMOVES 'http://', 'https://' or 'ftp://' FROM THE START
    url = url.replace(new RegExp(/^http:\/\/|^https:\/\/|^ftp:\/\//i), '');

    // IF THERE, REMOVES 'www.' FROM THE START OF THE STRING
    url = url.replace(new RegExp(/^www\./i), '');

    // REMOVE COMPLETE STRING FROM FIRST FORWARD SLASH ON
    url = url.replace(new RegExp(/\/(.*)/), '');

    // REMOVES '.??.??' OR '.???.??' FROM END - e.g. '.CO.UK', '.COM.AU'
    if (url.match(new RegExp(/\.[a-z]{2,3}\.[a-z]{2}$/i))) {
      url = url.replace(new RegExp(/\.[a-z]{2,3}\.[a-z]{2}$/i), '');

      // REMOVES '.??' or '.???' or '.????' FROM END - e.g. '.US', '.COM', '.INFO'
    } else if (url.match(new RegExp(/\.[a-z]{2,4}$/i))) {
      url = url.replace(new RegExp(/\.[a-z]{2,4}$/i), '');
    }

    // CHECK TO SEE IF THERE IS A DOT '.' LEFT IN THE STRING
    const subDomain = !!url.match(new RegExp(/\./g));

    return subDomain;
  },

  getDomain(url) {
    let hostname;

    if (url.indexOf('://') > -1) {
      hostname = url.split('/')[2];
    } else {
      hostname = url.split('/')[0];
    }
    hostname = hostname.split(':')[0];
    hostname = hostname.split('?')[0];
    if (this.isSubDomain(hostname)) {
      hostname = hostname.substring(hostname.indexOf('.') + 1);
    }
    return hostname;
  },

  parseRGBA,
  getTransformCSSFromObject,
  getContrastColor,
  getNormalizedRGBAString,
  getRGBStringFromRGBObject,
  getRGBAStringFromRGBAObject,
  getRGBAObjectFromColorPickerObject,
  getInterpolatedRGBOjectFromRGBColors,
  getRGBAStringFromColorPickerObject,

  removeCharStyles(property, object) {
    const removeStyles = obj => {
      if (obj.type === 'activeSelection') {
        obj.forEachObject(o => {
          removeStyles(o);
        });
        return;
      }
      if (obj.styles) {
        const styles = obj.styles;
        Object.keys(styles).forEach(row => {
          Object.keys(styles[row]).forEach(char => {
            if (property in styles[row][char]) {
              delete styles[row][char][property];
            }
          });
        });
      }
    };
    removeStyles(object);
  },

  getVariants(fontFamily, availableFonts) {
    return map(find(availableFonts, { name: fontFamily }).variants, 'weight');
  },

  hasVariants(fontFamily, availableFonts, variantsToFind) {
    const availableVariants = this.getVariants(fontFamily, availableFonts);
    const commonVariants = this.intersection([
      availableVariants,
      variantsToFind,
    ]);
    return isEqual(variantsToFind, commonVariants);
  },

  getSelectionStyles(object, start, end, complete) {
    if (start - end === 0) {
      return [];
    }
    let styles = object.isEditing ? object.getSelectionStyles() : [];
    if (styles.length === 0) {
      styles = [
        {
          fontWeight: object.fontWeight,
          fontStyle: object.fontStyle,
          fontFamily: object.fontFamily,
          fontSize: object.fontSize,
          fill: object.fill,
          underline: object.underline,
          linethrough: object.linethrough,
          styleCase: object.styleCase,
        },
      ];
    }
    return styles;
  },

  isValidSecureURL(str) {
    return (
      typeof validate.single(str, { url: { schemes: ['https'] } }) ===
      'undefined'
    );
  },

  isValidURL,
  isValidHttpURL,
  isValidEmail,

  isValidImageURL(str) {
    if (typeof validate.single(str, { url: true }) !== 'undefined') {
      return false;
    }

    const parsedUrl = new URL(str);
    return /\.(jpg|jpeg|gif|png)$/i.test(parsedUrl.pathname);
  },

  isValidHyperlink(str) {
    if (str.toLowerCase() === 'previous' || str.toLowerCase() === 'next') {
      return true;
    }
    if (
      str.substr(0, 1) === '#' &&
      typeof validate.single(str.substr(1), { numericality: true }) ===
        'undefined'
    ) {
      return true;
    }

    return isValidURL(str, true);
  },

  isSafeHyperlink(str) {
    return new Promise((resolve, reject) => {
      const payload = {
        client: {
          clientId: 'ludus',
          clientVersion: '1.0.0',
        },
        threatInfo: {
          threatTypes: ['SOCIAL_ENGINEERING'],
          platformTypes: ['ALL_PLATFORMS'],
          threatEntryTypes: ['URL'],
          threatEntries: [{ url: str }],
        },
      };

      request
        .post(
          `https://safebrowsing.googleapis.com/v4/threatMatches:find?key=${GOOGLE_API_KEY}`,
        )
        .send(payload)
        .end((error, response) => {
          if (error || !response.ok) {
            console.error(error);
            resolve(true);
          } else {
            const result = JSON.parse(response.text);
            resolve(!result.matches);
          }
        });
    });
  },

  isValidUUID(str) {
    return !!str.match(
      new RegExp(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
      ),
    );
  },

  keyToPrettyListLabel(key) {
    return LIST_LABELS[key];
  },

  prettyToKeyListLabel(pretty) {
    return Object.keys(LIST_LABELS).find(key => LIST_LABELS[key] === pretty);
  },

  isSVGString(str) {
    return (
      str.includes('xmlns="http://www.w3.org/2000/svg"') ||
      str.includes("xmlns='http://www.w3.org/2000/svg'")
    );
  },

  isValidSlug(str) {
    return !!str.match(new RegExp(/^[a-z0-9]+(?:-[a-z0-9]+)*$/));
  },

  isValidPassword(str) {
    return !str.includes('\\');
  },

  isMobileDevice() {
    const a = navigator.userAgent || navigator.vendor || window.opera;
    let check = false;
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
    return check;
  },

  isEmbed() {
    return window.self !== window.top;
  },

  isAppVisible() {
    const hidden =
      document.hidden || document.msHidden || document.webkitHidden || false;
    return !hidden;
  },

  isEventFromTextfield(e) {
    return (
      (e.target &&
        ['INPUT', 'TEXTAREA'].includes(e.target.nodeName) &&
        !e.target.readOnly) ||
      e.target.getAttribute('className') === 'hidden' ||
      e.target.isContentEditable ||
      e.target.getAttribute('data-text')
    );
  },

  supportsGestureEvents() {
    return 'GestureEvent' in window;
  },

  stopPropagation(e) {
    e.stopPropagation();
  },

  preventDefault(e) {
    e.preventDefault();
  },

  fullscreenEnabled() {
    return (
      document.fullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.msFullscreenEnabled
    );
  },

  requestFullscreen() {
    const element = document.documentElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  },

  exitFullScreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  },

  roundPerScale(n, scale) {
    const decimals = n.toString().split('.')[1].length;
    return (Math.round(n / scale) * scale).toFixed(decimals);
  },

  charLength(number) {
    return `${number}`.length;
  },

  leftPad(number, length) {
    let str = `${number}`;
    while (str.length < length) {
      str = `0${str}`;
    }
    return str;
  },

  getBoxShadowFromFabricShadow(shadow, angle, scale = 1) {
    if (!shadow) {
      return 'none';
    }

    const shadowVectorLength = Math.sqrt(
      shadow.offsetX ** 2 + shadow.offsetY ** 2,
    );
    const shadowVectorAngle =
      Math.atan2(shadow.offsetY, shadow.offsetX) - (angle * Math.PI) / 180;

    const shadowColor = shadow.color;
    const shadowBlur = shadow.blur * scale;
    const shadowOffsetX =
      Math.cos(shadowVectorAngle) * shadowVectorLength * scale;
    const shadowOffsetY =
      Math.sin(shadowVectorAngle) * shadowVectorLength * scale;

    return `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowColor}`;
  },

  getDropShadowAngleFromFabricShadow(shadow) {
    if (!shadow) {
      return 0;
    }
    const angle = (Math.atan2(shadow.offsetY, shadow.offsetX) * 180) / Math.PI;
    return angle;
  },

  getDropShadowCSSFilterFromFabricShadow(
    shadow,
    angle,
    scale = 1,
    useBoxShadow = false,
  ) {
    if (!shadow) {
      return 'none';
    }

    const shadowVectorLength = Math.sqrt(
      shadow.offsetX ** 2 + shadow.offsetY ** 2,
    );
    const shadowVectorAngle =
      Math.atan2(shadow.offsetY, shadow.offsetX) - (angle * Math.PI) / 180;
    let shadowBlurMultiplier = 0.35;
    if (isEdge) {
      shadowBlurMultiplier = useBoxShadow ? 0.225 * 1.5 : 0.225;
    } else if (isChrome) {
      shadowBlurMultiplier = useBoxShadow
        ? shadowBlurMultiplier * 1.5
        : shadowBlurMultiplier;
    } else if (isSafari) {
      shadowBlurMultiplier = useBoxShadow
        ? shadowBlurMultiplier * 1.5
        : shadowBlurMultiplier;
    } else if (isFirefox) {
      shadowBlurMultiplier = useBoxShadow ? 0.225 * 1.5 : 0.225;
    }

    const shadowColor = shadow.color;
    const shadowBlur = shadow.blur * shadowBlurMultiplier * scale;
    const shadowOffsetX =
      Math.cos(shadowVectorAngle) * shadowVectorLength * scale;
    const shadowOffsetY =
      Math.sin(shadowVectorAngle) * shadowVectorLength * scale;
    if (useBoxShadow) {
      return `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowColor}`;
    }
    return `drop-shadow(${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowColor})`;
  },

  asyncLoop(iterations, func, callback) {
    let index = 0;
    let done = false;
    const loop = {
      next() {
        if (done) {
          return;
        }
        if (index < iterations) {
          index += 1;
          func(loop);
        } else {
          done = true;
          callback && callback();
        }
      },
      iteration() {
        return index - 1;
      },
      break() {
        done = true;
        callback();
      },
    };
    loop.next();
    return loop;
  },

  promiseSerial: promiseFactories =>
    promiseFactories.reduce(
      (serialPromise, promiseFactory) =>
        serialPromise.then(results =>
          promiseFactory().then(promiseResult => results.concat(promiseResult)),
        ),
      Promise.resolve([]),
    ),

  // function to copy without using async clipboard API
  // Used when we have to write the clipboard without accessing
  // clipboard event. IE: save in clipboard via a button.
  copy(text) {
    const el = document.createElement('textarea');

    el.value = text;

    // Prevent keyboard from showing on mobile
    el.setAttribute('readonly', '');
    el.setAttribute('className', 'hidden');

    el.style.contain = 'strict';
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    el.style.fontSize = '12pt'; // Prevent zooming on iOS

    const selection = getSelection();
    let originalRange = false;
    if (selection.rangeCount > 0) {
      originalRange = selection.getRangeAt(0);
    }

    document.body.appendChild(el);
    el.select();

    // Explicit selection workaround for iOS
    el.selectionStart = 0;
    el.selectionEnd = text.length;

    let success;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      success = false;
    }

    document.body.removeChild(el);

    if (originalRange) {
      selection.removeAllRanges();
      selection.addRange(originalRange);
    }

    return success;
  },

  copyToClipboard() {
    if (utils.isChrome) {
      document.execCommand('copy');
    } else {
      smalltalk.alert(
        'Oops',
        `Sorry, this feature only works in Google Chrome. Alternatively, you can use the keyboard shortcut ${
          utils.isMac ? '(⌘C)' : '(Ctrl+C)'
        } to copy content.`,
      );
    }
  },

  pasteFromClipboard(activeObject, fabricCanvas, paste, pasteAction) {
    if (navigator.clipboard && navigator.clipboard.readText) {
      navigator.permissions
        .query({
          name: 'clipboard-read',
        })
        .then(permissionStatus => {
          // Will be 'granted', 'denied' or 'prompt':
          if (permissionStatus.state !== 'granted') {
            smalltalk.alert(
              'Oops',
              `Sorry, you need to authorize Ludus to access your clipboard data. Alternatively, you can use the keyboard shortcut ${
                utils.isMac ? '(⌘V)' : '(Ctrl+V)'
              } to paste content.`,
            );
          }
        });

      navigator.clipboard.readText().then(text => {
        if (text.length === 0) {
          smalltalk.alert(
            'Oops',
            `Sorry, it seems we can't access your clipboard data. Alternatively, you can use the keyboard shortcut ${
              utils.isMac ? '(⌘V)' : '(Ctrl+V)'
            } to paste content.`,
          );
        } else if (activeObject && activeObject.isEditing) {
          // if object is editing we have to simulate CMD-V
          try {
            const result = utils.parseJSONForTextValue(text);
            if (typeof result === 'string') {
              utils.insertInTextArea(activeObject.hiddenTextarea, result);
              activeObject.updateFromTextArea();
              fabricCanvas.requestRenderAll();
            } else {
              activeObject.exitEditing();
              // wait just enough for text to be exited and selected
              // otherwise, the text will get selected back
              // just after the added obj has been selected
              setTimeout(() => {
                paste(result);
              }, 5);
              return;
            }
          } catch (error) {
            utils.insertInTextArea(activeObject.hiddenTextarea, text);
            activeObject.updateFromTextArea();
            fabricCanvas.requestRenderAll();
          }
        } else {
          pasteAction(text);
        }
      });
    } else {
      smalltalk.alert(
        'Oops',
        `Sorry, this feature only works in Google Chrome. Alternatively, you can use the keyboard shortcut ${
          utils.isMac ? '(⌘V)' : '(Ctrl+V)'
        } to paste content.`,
      );
    }
  },

  /**
   * Check if user has a specific feature (activated in the backend)
   *
   * @param featureCode
   */
  hasFeature(featureCode) {
    const features = get(window, 'user.features', []);

    return features.includes(featureCode);
  },

  postMessage(target, data) {
    if (target) {
      target.postMessage(data, '*');
    }
  },

  addLudusIds,
  removeLudusIds,
  cleanValues,
  cleanValuesDeep,
  safeGet,
  schedule,
  smartRound,
  advancedQuerySelector,
  toSentence,
  getUrlParameter,
  getEditorWindowNameForId,
  getViewerWindowNameForId,
  getEmbedWindowNameForId,
  getDashboardWindowName,
  getSpeakerWindowName,
  getEmailsFromString,
  getDefaultTheme,
  checkSVGString,
  getSizeFromViewBox,
};

export default utils;
